import {addNotice, removeNotice} from "./notices";

/**
 * SignupUpdateUser. Will fire any ajax call with all input fields as data.
 */
export default class SignupUpdateUser {
  constructor(id = 'td-signup-form-update-marketo-user', action = 'signup_update_marketo_user') {
    this.form = document.getElementById( id );
    this.action = action;

    if (!this.form) {
      return;
    }

    this.submitButton = this.form?.querySelector( '[type="submit"]' );
    this.requiredFields = this.form?.querySelectorAll( '[required]' );

    this.isFormValid = false;

    // Timer for the keyup event with delay/debounce.
    this.timer = null;
    this.waitTime = 100;

    this.captcha = window.signup_variables.captcha || {};
    this.turnstileToken = null;

    this.bindEvents();
    this.windowEvents();
    this.keyboardEvents();
    this.renderTurnstile();

    // Methods.
    this.form.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();

        if ( this.captcha.type === 'recaptcha' && this.captcha.key ) {
          grecaptcha.ready( () => grecaptcha.execute( this.captcha.key, {action: 'submit'} ).then( () => this.ajaxCall() ) );
        } else if ( this.captcha.type === 'turnstile' && this.turnstileToken ) {
          this.ajaxCall();
        } else if ( this.captcha.type === '' ) {
          this.ajaxCall();
        }
      }
    );
  }

  renderTurnstile() {
    if ( this.captcha.type !== 'turnstile' || ! this.captcha.key ) {
      return;
    }

    const container = document.createElement( 'div' );
    this.form.querySelector( '[type="submit"]' ).before( container );

    turnstile.render( container, {
      sitekey: this.captcha.key,
      callback: ( token ) => { this.turnstileToken = token; },
    } );
  }

  /**
   *
   * @param key The key of the input-field.
   * @param isVisible If the input-field should be visible or not, if visible, set required.
   */
  displayFieldIfChecked( key, isVisible = false ) {
    const parent = this.form.elements[key].closest( '.td-form-field' );
    isVisible ? parent.classList.remove( 'hidden' ) : parent.classList.add( 'hidden' );
  }

  keyboardEvents() {

    // Detect if user has CapsLock enabled.
    document.addEventListener( 'keydown', (event) => {
      if (!!event.getModifierState( 'CapsLock' )) {
        addNotice( window.signup_variables.errors.capslock_on, this.form, 'capslock-notification', 'alert' );
      } else {
        removeNotice( 'capslock-notification' );
      }
    } );


    this.requiredFields?.forEach( field => {

      if (field.type === 'checkbox') {
        if (field.dataset && field.dataset.requireField) {
          this.displayFieldIfChecked( field.dataset.requireField, field.checked );
        }

        field.addEventListener( 'change', (e) => {
          e.preventDefault();

          if (field.dataset && field.dataset.requireField) {
            this.displayFieldIfChecked( field.dataset.requireField, field.checked );
          }

          this.validateField( field );
        } );

      } else {
        field.addEventListener(
          'keyup',
          (event) => {
            if (!!event.getModifierState( 'CapsLock' )) {
              addNotice( window.signup_variables.errors.capslock_on, this.form, 'signup-notification', 'alert' );
            } else {
              removeNotice( 'signup-notification' );
            }
            this.validateField( field )
          }
        );
      }

      field.addEventListener( 'blur', (e) => {
          e.preventDefault();
          this.validateField( field )
        }
      );
    } );
  }

  /**
   * Bind events to the class.
   */
  bindEvents() {
    this.validateForm = this.validateForm.bind( this );
  }

  windowEvents() {
    window.addEventListener( 'validateForm', this.validateForm );
  }

  /**
   * Detect if the form is valid.
   * Enables or disables the form-ajax request.
   * On a valid form it will remove the error messages.
   */
  validateForm() {
    const fieldsToCheck = {};
    // Set all form fields to an object.
    // If the field is a checkbox, set the value to true if checked.
    // If the field is not a checkbox, set the value to the trimmed value.
    Array.from( this.requiredFields )?.forEach( (input) => {
      if (input.type === 'checkbox') {
        fieldsToCheck[input.name] = input.checked;
        return
      }
      fieldsToCheck[input.name] = input.value.trim()
    } );

    let isValid;

    // Set isValid to true if all required values are not empty.
    Object.values( fieldsToCheck )?.includes( '' ) || Object.values( fieldsToCheck )?.includes( false ) ? isValid = false : isValid = true;

    this.submitButton.disabled = !isValid;
    this.isFormValid = isValid;
  }

  /**
   * Validate the field.
   * @param {object} field the field to validate.
   */
  validateField(field) {
    if ((field.type === 'checkbox' && !field.checked) || (field.type !== 'checkbox' && field.value.trim() === '')) {
      field.classList.add( 'invalid' );
    } else {
      field.classList.remove( 'invalid' );
    }

    clearTimeout( this.timer );
    this.timer = setTimeout( () => window.dispatchEvent( new CustomEvent( "validateForm" ) ), this.waitTime );
  }

  /**
   * Get all input fields and return them as a data string.
   *
   * @return {string}
   */
  fieldsToDataString() {
    const dataObject = {
      action: this.action,
    };
    Array.from( this.form.elements )?.forEach( (input) => dataObject[input.name] = input.value.trim() );
    return new URLSearchParams( dataObject ).toString();
  }

  ajaxCall() {
    if (!this.form) {
      return;
    }

    fetch(
      window.signup_variables.ajax_url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: this.fieldsToDataString()
      }
    )
      .then( response => response.json() )
      .then(
        response => {
          if (response.success) {
            removeNotice( 'signup-user-update-status' );
            if (response.data.message) {
              addNotice( response.data.message, this.form, 'signup-user-update-status', 'success' );
            }

            if (response.data.redirect) {
              window.location.href = response.data.redirect;
            }
          } else {
            addNotice( response.data.message, this.form, 'signup-user-update-status', 'error' );
          }
        }
      )
      .finally(
        () => {
          this.form.classList.remove( 'loading' );
        }
      )
      .catch(
        error => {
          removeNotice( 'signup-user-update-status' );
          console.error( 'AJAX error:', error );
        }
      );
  }
}
